// ABOUTME: Workspace list page showing all workspaces with search and filtering.
// ABOUTME: Displays subscription tier, status, and quick stats for each workspace.

import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search, Building2, ChevronRight, Filter } from 'lucide-react';
import { workspaceApi } from '@/services/api/adminClient';
import type { WorkspaceListItem, SubscriptionTier } from '@/types/admin';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { CreateWorkspaceModal } from './CreateWorkspaceModal';
import { ProvisioningStatus } from '@/components/admin/ProvisioningStatus';
import { UsageProgressBar } from '@/components/admin/UsageProgressBar';

const TIERS: SubscriptionTier[] = ['freemium', 'basic', 'standard', 'enterprise', 'enterprise_plus'];

export function WorkspaceList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [workspaces, setWorkspaces] = useState<WorkspaceListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const page = parseInt(searchParams.get('page') || '1', 10);
  const search = searchParams.get('search') || '';
  const tierFilter = searchParams.get('tier') || '';

  useEffect(() => {
    async function loadWorkspaces() {
      try {
        setLoading(true);
        const response = await workspaceApi.list({
          page,
          page_size: 20,
          search: search || undefined,
          subscription_tier: tierFilter || undefined,
        });
        setWorkspaces(response.workspaces);
        setTotal(response.total);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load workspaces');
      } finally {
        setLoading(false);
      }
    }
    loadWorkspaces();
  }, [page, search, tierFilter]);

  const handleSearch = (value: string) => {
    const params = new URLSearchParams(searchParams);
    if (value) {
      params.set('search', value);
    } else {
      params.delete('search');
    }
    params.set('page', '1');
    setSearchParams(params);
  };

  const handleTierFilter = (tier: string) => {
    const params = new URLSearchParams(searchParams);
    if (tier) {
      params.set('tier', tier);
    } else {
      params.delete('tier');
    }
    params.set('page', '1');
    setSearchParams(params);
  };

  const handlePageChange = (newPage: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', String(newPage));
    setSearchParams(params);
  };

  const handleWorkspaceCreated = () => {
    setShowCreateModal(false);
    // Reload workspaces by resetting to page 1
    const params = new URLSearchParams(searchParams);
    params.set('page', '1');
    setSearchParams(params);
  };

  const totalPages = Math.ceil(total / 20);

  if (error) {
    return (
      <div className="bg-error-light border border-error/30 rounded-sm p-4 text-error">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-surface-800">Workspaces</h1>
          <p className="text-sm text-surface-600 mt-1">
            {total} workspaces total
          </p>
        </div>
        <Button onClick={() => setShowCreateModal(true)}>
          New Workspace
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <Input
            placeholder="Search workspaces..."
            defaultValue={search}
            leftIcon={<Search className="w-4 h-4" />}
            onChange={(e) => {
              const value = e.target.value;
              // Debounce search
              const timeout = setTimeout(() => handleSearch(value), 300);
              return () => clearTimeout(timeout);
            }}
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-surface-500" />
          <select
            value={tierFilter}
            onChange={(e) => handleTierFilter(e.target.value)}
            className="px-4 py-3 rounded-sm bg-surface-0 border border-surface-200 text-surface-800 text-sm focus:ring-1 focus:ring-primary-600/25 focus:border-primary-600/50"
          >
            <option value="">All Tiers</option>
            {TIERS.map((tier) => (
              <option key={tier} value={tier}>
                {tier.replace('_', ' ')}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Workspace List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        </div>
      ) : workspaces.length === 0 ? (
        <Card variant="glass" className="p-8 text-center">
          <Building2 className="w-12 h-12 text-surface-500 mx-auto mb-4" />
          <p className="text-surface-600">No workspaces found</p>
        </Card>
      ) : (
        <div className="space-y-4">
          {workspaces.map((workspace) => (
            <Card key={workspace.id} variant="surface" className="hover:border-primary-600/30">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Link to={`/admin/workspaces/${workspace.id}`} className="flex items-center gap-4 flex-1">
                    <div className="w-10 h-10 rounded-sm bg-primary-50 flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-primary-600" />
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-surface-800">
                        {workspace.name}
                      </h3>
                      <p className="text-xs text-surface-500">{workspace.slug}</p>
                    </div>
                  </Link>

                  <div className="flex items-center gap-6">
                    {/* Provisioning Status */}
                    <ProvisioningStatus
                      workspaceId={workspace.id}
                      spStatus={workspace.sp_status}
                      spErrorMessage={workspace.sp_error_message}
                      spCreatedAt={workspace.sp_created_at}
                      hasWriteableCatalog={workspace.has_writeable_catalog}
                      catalogName={workspace.catalog_name}
                      catalogCreatedAt={workspace.catalog_created_at}
                      onRetrySuccess={() => {
                        // Reload workspaces after successful retry
                        const params = new URLSearchParams(searchParams);
                        setSearchParams(params);
                      }}
                    />

                    <div className="text-right mr-4">
                      <p className="text-xs text-surface-500">Data Sources</p>
                      <p className="text-sm font-medium text-surface-800">
                        {workspace.data_sources_count}
                      </p>
                    </div>
                    <TierBadge tier={workspace.subscription_tier} />
                    <SubscriptionStatusBadge status={workspace.subscription_status} />

                    <Link to={`/admin/workspaces/${workspace.id}`}>
                      <ChevronRight className="w-4 h-4 text-surface-500 hover:text-surface-800" />
                    </Link>
                  </div>
                </div>

                {/* Usage progress bars */}
                {workspace.limits && workspace.current_usage && (
                  <div className="pl-14 pr-4 space-y-2 pt-2 border-t border-surface-100">
                    <UsageProgressBar
                      label="Users"
                      used={workspace.current_usage.users}
                      limit={workspace.limits.max_users}
                    />
                    <UsageProgressBar
                      label="Messages/mo"
                      used={workspace.current_usage.messages}
                      limit={workspace.limits.max_messages_per_month}
                    />
                    <UsageProgressBar
                      label="Reports/mo"
                      used={workspace.current_usage.reports}
                      limit={workspace.limits.max_reports_per_month}
                    />
                    <UsageProgressBar
                      label="Investigations/mo"
                      used={workspace.current_usage.investigations}
                      limit={workspace.limits.max_investigations_per_month}
                    />
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            disabled={page <= 1}
            onClick={() => handlePageChange(page - 1)}
          >
            Previous
          </Button>
          <span className="px-4 text-sm text-surface-600">
            Page {page} of {totalPages}
          </span>
          <Button
            variant="secondary"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => handlePageChange(page + 1)}
          >
            Next
          </Button>
        </div>
      )}

      {/* Create Workspace Modal */}
      <CreateWorkspaceModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreated={handleWorkspaceCreated}
      />
    </div>
  );
}

function TierBadge({ tier }: { tier: string }) {
  const colors: Record<string, string> = {
    freemium: 'bg-surface-100 text-surface-500',
    basic: 'bg-primary-50 text-primary-600',
    standard: 'bg-success-light text-success',
    enterprise: 'bg-warning-light text-warning',
    enterprise_plus: 'bg-error-light text-error',
  };

  return (
    <span className={`px-2.5 py-1 text-xs font-medium rounded-sm ${colors[tier] || colors.freemium}`}>
      {tier.replace('_', ' ')}
    </span>
  );
}

function SubscriptionStatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    active: 'bg-success-light text-success',
    trial: 'bg-warning-light text-warning',
    suspended: 'bg-error-light text-error',
    cancelled: 'bg-surface-100 text-surface-500',
  };

  return (
    <span className={`px-2.5 py-1 text-xs font-medium rounded-sm ${colors[status] || colors.active}`}>
      {status}
    </span>
  );
}

export default WorkspaceList;
